<script setup>
import {useWalletStore} from "@/stores/wallet.js";
import {useUserStore} from "@/stores/user.js";
import {onMounted, ref, provide, watch} from "vue";
import CreateWallet from "@/views/wallet/CreateWallet2.vue";
import {gregorianDate} from "@/utils/gregorianDate.js";
import {formatPrice} from '@/utils/priceFormatter.js';
import {useI18n} from "vue-i18n";
import {useRouter} from 'vue-router';
import Icon from "@/components/Icon/index.vue";
import SortIcon from "@/components/datatable/sortIcon.vue";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import AllButtons from "@/views/orders/Shared/AllButtons.vue";
import {FilterMatchMode} from "@primevue/core/api";

const props = defineProps({
  wallets: Array
})

const emit = defineEmits(['showTransactions']);
const {t, locale} = useI18n({useScope: 'global'})
const search = ref('');
const router = useRouter();
const columns = ref([
  {field: "asset", title: t("message.currency")},
  {field: 'amount', title: t("message.current balance"), type: 'number'},
  {field: 'lastChange', title: t("message.last change"), type: 'number'},
  {field: 'lastUpdate', title: t("message.last update")}
]);
const walletStore = useWalletStore();
const userStore = useUserStore();
const isShowCreateWalletModal = ref(false)
const width = ref(0);
const keyword = ref('');
const filters = ref({
  global: {value: null, matchMode: FilterMatchMode.CONTAINS},
})
watch(keyword, (val) => {
  filters.value.global.value = val
})

function showCreateWalletModal() {
  isShowCreateWalletModal.value = true
}

function closeCreateWalletModal() {
  isShowCreateWalletModal.value = false
}

function showTransactionsByAsset({data}) {
  walletStore.openSingle(data)
  walletStore.selectedAsset = data.asset
  walletStore.selectedUserID = data.userID
  walletStore.selectedUserName = data.username
}

const handleResize = () => {
  width.value = window.innerWidth;
};

onMounted(() => {
  window.addEventListener("resize", handleResize);
  handleResize();
});

provide('isShowCreateWalletModal', isShowCreateWalletModal)
</script>

<template>
  <div class="h-full">
    <div class="flex flex-col gap-4 w-full h-full">
      <div class="flex flex-wrap justify-between gap-4 w-full px-2 pt-2 lg:px-4 lg:pt-4">
        <div class="flex flex-row gap-2">
          <div class="lg:hidden">
            <div
                @click="walletStore.openMobileWalletSidebar()"
                class="md:h-8 md:w-8 h-6 w-6 bg-slate-100 dark:text-slate-300 dark:bg-slate-900 flex flex-col justify-center items-center md:text-base text-sm rounded-full cursor-pointer"
            >
              <Icon icon="heroicons-outline:menu-alt-2"/>
            </div>
          </div>
          <p class="text-lg text-white">{{ $t('message.assets') }}</p>
        </div>
        <AllButtons v-model="keyword" :showFilter="false" :showCategoriesButtons="false" :copyButton="false"
                    :buttonForNewItem="true" :titleForNewItem="t('message.create wallet')"
                    @buttonForNewItemClicked="showCreateWalletModal"/>
      </div>
      <DataTable
          :nullSortOrder="-1"
          :value="wallets"
          scrollable
          scrollHeight="flex"
          :virtualScrollerOptions="{ itemSize: 30 }"
          resizableColumns
          removableSort
          stripedRows
          :filters="filters"
          class="text-sm"
          size="small"
          tableStyle=""
          row-hover
          @row-click="showTransactionsByAsset"
          :rowClass="() => 'hover:!bg-[#2c4967] even:!bg-[#1A2941] odd:!bg-[#121E31] !text-slate-300 cursor-pointer'"
      >
        <Column field="asset" :header="t('message.currency')" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap !py-0"
                style="width: 5%; height: 30px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
        </Column>
        <Column field="amount" :header="t('message.current balance')" dataType="numeric" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap !py-0"
                style="width: 50%; min-width: 100px; height: 30px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
            <span class="block w-full text-white"
                  :class="{ '!text-red-400': slotProps.data.amount < 0, '!text-success-500': slotProps.data.amount > 0 }">
              {{ slotProps.data.amount ? formatPrice(slotProps.data.amount) : '' }}
            </span>
          </template>
        </Column>
        <Column field="lastChange" :header="t('message.last change')" dataType="numeric" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 8%; min-width: 100px; height: 30px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
            <span class="block w-full text-white"
                  :class="{ '!text-red-400': slotProps.data.lastChange < 0, '!text-success-500': slotProps.data.lastChange > 0 }">
                <template v-if="slotProps.data.lastChange <= 0">
                  {{ slotProps.data.lastChange ? formatPrice(slotProps.data.lastChange) : '' }}
                </template>
                <template v-else>
                  {{ slotProps.data.lastChange ? '+' + formatPrice(slotProps.data.lastChange) : '' }}
                </template>
            </span>
          </template>
        </Column>
        <Column field="lastUpdate" :header="t('message.last update')" dataType="numeric" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 8%; min-width: 100px; height: 30px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
            <span class="block w-full max-w-max">
              {{ slotProps.data.lastUpdate ? gregorianDate(slotProps.data.lastUpdate, 'HH:mm - DD MMM YYYY') : 'ــــ' }}
            </span>
          </template>
        </Column>
        <template #empty>
          <h1 class="py-2 text-center text-lg">There is no asset</h1>
        </template>
      </DataTable>
    </div>
    <CreateWallet v-if="isShowCreateWalletModal" @closeModal="closeCreateWalletModal"/>
  </div>
</template>

<style scoped lang="scss">

</style>